import { Dexie } from 'dexie';
import { Base64 } from 'js-base64';
import pako from 'pako';

const drivers: { [name: string]: BaseDummyDriver } = {};
/**
 *
 * @param tableName
 */
const getDriver = (tableName): BaseDummyDriver => drivers[tableName];

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });

/**
 *
 */
class BaseDummyDriver {
  protected localTbl: any = {};
  protected tableName = '';
  protected primaryKeyName = '';
  protected secondaryKeyName = '';
  public gsiPrimaryKeyName = '';

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected clearFunc = async () => {};
  public allLocalTableClear = async () => {
    await this.clearFunc();
  };

  public openDb = (tableDefs?: any, version = 6) => {
    const db = new Dexie(this.tableName);
    if (!tableDefs) {
      tableDefs = {
        mainTbl: `[${this.primaryKeyName}+${this.secondaryKeyName}]`,
        mediaTbl: 'filePath',
      };
    }
    db.version(version).stores(tableDefs);
    db.open();
    Object.keys(tableDefs).forEach((name) => {
      this.localTbl[name.replace('Tbl', '')] = (db as any)[name];
    });
    this.localTbl.db = db;
  };

  /**
   *
   * @param table
   * @param primaryKey
   * @param secondaryKey
   * @param tableDefs
   */
  constructor(table: string, primaryKey: string, secondaryKey: string, tableDefs?: any) {
    this.tableName = `${table}DummyDriver`;
    this.primaryKeyName = primaryKey;
    this.secondaryKeyName = secondaryKey;
    this.openDb(tableDefs);
    this.clearFunc = async () => {
      await this.localTbl.db.delete();
      //await sleep(1000);
      this.openDb(tableDefs);
    };
    drivers[table] = this;
  }

  protected setDriverParameters = (db: any, table: string, primaryKey: string, secondaryKey: string) => {
    this.tableName = `${this.tableName}${table}`;
    this.primaryKeyName = primaryKey;
    this.secondaryKeyName = secondaryKey;
    this.localTbl = db;
  };

  public setGsiKey = (gsi: string) => (this.gsiPrimaryKeyName = gsi);

  public dummyCreateAdapter = async (record: any) => {
    record.updatedAt = new Date().getTime();

    await this.localTbl.main.put(record);
    return { updatedAt: record.updatedAt };
  };

  public dummyBulkCreateAdapter = async (datB64: string): Promise<string> => {
    const now = new Date().getTime();
    const resp: { updatedAt: number }[] = [];

    const dat = JSON.parse(pako.inflate(Base64.toUint8Array(datB64), { to: 'string' }));
    for (const d of dat) {
      d.updatedAt = now;

      await this.localTbl.main.put(d);
      resp.push({
        [this.primaryKeyName]: d[this.primaryKeyName],
        [this.secondaryKeyName]: d[this.secondaryKeyName],
        updatedAt: now,
      });
    }
    return Base64.fromUint8Array(pako.deflate(JSON.stringify(resp)));
  };

  public dummyUpdateAdapter = async (record: any) => {
    record.updatedAt = new Date().getTime();
    let entry = await this.localTbl.main.get([record[this.primaryKeyName], record[this.secondaryKeyName]]);
    if (!entry) return;

    entry = { ...entry, ...record };

    await this.localTbl.main.put(entry);
    return { updatedAt: record.updatedAt };
  };

  public dummyBulkUpdateAdapter = async (datB64: string): Promise<string> => {
    const now = new Date().getTime();
    const resp: { updatedAt: number }[] = [];
    const dat = JSON.parse(pako.inflate(Base64.toUint8Array(datB64), { to: 'string' }));
    for (const d of dat) {
      let entry = await this.localTbl.main.get([d[this.primaryKeyName], d[this.secondaryKeyName]]);
      if (!entry) continue;
      d.updatedAt = now;

      entry = { ...entry, ...d };

      await this.localTbl.main.put(entry);
      resp.push({
        [this.primaryKeyName]: entry[this.primaryKeyName],
        [this.secondaryKeyName]: entry[this.secondaryKeyName],
        updatedAt: now,
      });
    }
    return Base64.fromUint8Array(pako.deflate(JSON.stringify(resp)));
  };

  public dummyDeleteAdapter = async (record: any) => {
    // EXP: 画像があれば一緒に消す
    if (this.secondaryKeyName !== '') {
      record = await this.localTbl.main.get([record[this.primaryKeyName], record[this.secondaryKeyName]]);
    } else {
      record = await this.localTbl.main.get(record[this.primaryKeyName]);
    }
    if (!record) return false;

    if (this.secondaryKeyName !== '') {
      await this.localTbl.main.delete([record[this.primaryKeyName], record[this.secondaryKeyName]]);
    } else {
      await this.localTbl.main.delete(record[this.primaryKeyName]);
    }
    return true;
  };

  public dummyReadAdapter2 = async (primaryKeyValue: string, timestamp = 0, filter: { [key: string]: any } | undefined) => {
    const allEntries = await this.localTbl.main
      .where([this.primaryKeyName, this.secondaryKeyName])
      .between([primaryKeyValue, Dexie.minKey], [primaryKeyValue, Dexie.maxKey], true, true)
      .toArray();
    const candidate = allEntries
      .filter((ent: any) => {
        if (ent.updatedAt < timestamp) return false;
        if (!filter) return true;
        return Object.keys(filter).every((k) => ent[k] === filter[k]);
      })
      .sort((a: any, b: any) => a.updatedAt - b.updatedAt);
    return Base64.fromUint8Array(pako.deflate(JSON.stringify(candidate)));
  };

  public dummyReadAllAdapter = async () => {
    const candidate = this.localTbl.main.toArray();
    return Base64.fromUint8Array(pako.deflate(JSON.stringify(candidate)));
  };

  public dummyReadGsiAdapter = async (primaryKeyValue: string, timestamp = 0, filter: { [key: string]: any } | undefined) => {
    const allEntries = await this.localTbl.main.toArray();
    const candidate = allEntries
      .filter((ent: any) => {
        if (ent[this.primaryKeyName] !== primaryKeyValue && ent[this.gsiPrimaryKeyName] !== primaryKeyValue) return false;
        if (ent.updatedAt < timestamp) return false;
        if (!filter) return true;
        return Object.keys(filter).every((k) => ent[k] === filter[k]);
      })
      .sort((a: any, b: any) => a.updatedAt - b.updatedAt);
    return Base64.fromUint8Array(pako.deflate(JSON.stringify(candidate)));
  };
}

export { BaseDummyDriver, getDriver };
